import React, { useEffect, useMemo, useState } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { GetConfigHealth } from "../global/Apis";

interface PromotionCandidate {
  key: string;
}

interface RegionPresence {
  key: string;
  usa: boolean;
  eur: boolean;
  can: boolean;
}

interface ConfigHealthData {
  counts: {
    development: number;
    staging: number;
    production: number;
  };
  ready_for_staging: PromotionCandidate[];
  ready_for_production: RegionPresence[];
  regional_drift: RegionPresence[];
}

type HealthTab = "drift" | "staging" | "production";

const PAGE_SIZE = 8;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const splitServiceAndKey = (fullKey: string): { service: string; rest: string } => {
  const firstColon = fullKey.indexOf(":");
  if (firstColon === -1) return { service: fullKey, rest: "" };
  return {
    service: fullKey.slice(0, firstColon),
    rest: fullKey.slice(firstColon + 1),
  };
};

const groupByService = <T extends { key: string }>(items: T[]): Record<string, T[]> => {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    const { service } = splitServiceAndKey(item.key);
    if (!groups[service]) groups[service] = [];
    groups[service].push(item);
  }
  return groups;
};

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}> = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;

  const pages: (number | "ellipsis")[] = [];
  const windowSize = 1;

  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - windowSize && p <= page + windowSize)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "ellipsis") {
      pages.push("ellipsis");
    }
  }

  return (
    <div className="flex items-center justify-center gap-1 px-5 py-4">
      <button
        type="button"
        aria-label="Previous page"
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className="inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors duration-150"
      >
        ‹
      </button>
      {pages.map((p, idx) =>
        p === "ellipsis" ? (
          <span key={`ellipsis-${idx}`} className="px-1.5 text-sm text-gray-400">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-colors duration-150 ${
              p === page
                ? "bg-indigo-600 text-white"
                : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Next page"
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        className="inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors duration-150"
      >
        ›
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Drift list — used only by the Drift tab. Purely informational, no action.
// ---------------------------------------------------------------------------

const RegionMark: React.FC<{ present: boolean }> = ({ present }) =>
  present ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-auto text-emerald-600" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-auto text-rose-500" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );

const DriftList: React.FC<{ rows: RegionPresence[]; searchTerm: string }> = ({ rows, searchTerm }) => {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.key.toLowerCase().includes(term));
  }, [rows, searchTerm]);

  const groups = useMemo(() => groupByService(filtered), [filtered]);
  const serviceNames = useMemo(() => Object.keys(groups).sort(), [groups]);

  const totalPages = Math.max(1, Math.ceil(serviceNames.length / PAGE_SIZE));
  const pagedServiceNames = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return serviceNames.slice(start, start + PAGE_SIZE);
  }, [serviceNames, page]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, rows]);

  const toggleGroup = (service: string) => {
    setExpanded((prev) => ({ ...prev, [service]: !prev[service] }));
  };

  if (filtered.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm">
        {searchTerm ? "No keys match your search." : "All three production regions are in sync."}
      </div>
    );
  }

  return (
    <>
      <div className="divide-y divide-gray-100">
        {pagedServiceNames.map((service, groupIdx) => {
          const isOpen = expanded[service] ?? (groupIdx === 0 && page === 1);
          const rowsInGroup = groups[service];

          return (
            <div key={service}>
              <button
                type="button"
                onClick={() => toggleGroup(service)}
                className="w-full px-5 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors duration-150 text-left"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold text-gray-800">{service}</span>
                <span className="text-xs text-gray-500">
                  {rowsInGroup.length} {rowsInGroup.length === 1 ? "key" : "keys"}
                </span>
              </button>

              {isOpen && (
                <div className="px-5 py-3 pl-12 bg-gray-50">
                  <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
                    <div className="grid grid-cols-[2fr_1fr_1fr_1fr]">
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">Key</span>
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex justify-center text-xs font-semibold text-gray-500 uppercase tracking-wide">USA</span>
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex justify-center text-xs font-semibold text-gray-500 uppercase tracking-wide">EUR</span>
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex justify-center text-xs font-semibold text-gray-500 uppercase tracking-wide">CAN</span>

                      {rowsInGroup.map((entry) => {
                        const { rest } = splitServiceAndKey(entry.key);
                        return (
                          <React.Fragment key={entry.key}>
                            <p className="px-4 py-3 border-b border-gray-100 bg-rose-50/40 text-sm font-mono text-gray-700 truncate">{rest || entry.key}</p>
                            <div className="px-4 py-3 border-b border-gray-100 bg-rose-50/40 flex justify-center items-center">
                              <RegionMark present={entry.usa} />
                            </div>
                            <div className="px-4 py-3 border-b border-gray-100 bg-rose-50/40 flex justify-center items-center">
                              <RegionMark present={entry.eur} />
                            </div>
                            <div className="px-4 py-3 border-b border-gray-100 bg-rose-50/40 flex justify-center items-center">
                              <RegionMark present={entry.can} />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Production-ready list — used only by the Ready for production tab.
// Requires a region to be chosen before any row can be batch-selected.
// Per-row checkbox is disabled when the key already exists in that region.
// Keeps the per-row "Submit for approval" button for single-key quick action,
// plus a checkbox + batch action bar for submitting multiple keys at once.
// ---------------------------------------------------------------------------

type ProdRegion = "usa" | "eur" | "can";

const REGION_LABELS: Record<ProdRegion, string> = {
  usa: "USA",
  eur: "EUR",
  can: "CAN",
};

const ProductionReadyList: React.FC<{
  rows: RegionPresence[];
  searchTerm: string;
  emptyMessage: string;
  onSubmit: (keys: string[], region?: string) => void;
}> = ({ rows, searchTerm, emptyMessage, onSubmit }) => {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selectedRegion, setSelectedRegion] = useState<ProdRegion | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.key.toLowerCase().includes(term));
  }, [rows, searchTerm]);

  const groups = useMemo(() => groupByService(filtered), [filtered]);
  const serviceNames = useMemo(() => Object.keys(groups).sort(), [groups]);

  const totalPages = Math.max(1, Math.ceil(serviceNames.length / PAGE_SIZE));
  const pagedServiceNames = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return serviceNames.slice(start, start + PAGE_SIZE);
  }, [serviceNames, page]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, rows]);

  // Switching region invalidates any prior selection — a key selected because
  // it was missing from USA may not be missing from EUR, so don't carry it over.
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [selectedRegion]);

  const toggleGroup = (service: string) => {
    setExpanded((prev) => ({ ...prev, [service]: !prev[service] }));
  };

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isMissingFromSelectedRegion = (entry: RegionPresence): boolean =>
    selectedRegion !== null && !entry[selectedRegion];

  const handleBatchSubmit = () => {
    if (!selectedRegion || selectedKeys.size === 0) return;
    onSubmit(Array.from(selectedKeys), selectedRegion);
    setSelectedKeys(new Set());
  };

  if (filtered.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm">
        {searchTerm ? "No keys match your search." : emptyMessage}
      </div>
    );
  }

  return (
    <>
      {/* Region picker + batch action bar */}
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">Target region</span>
          {(Object.keys(REGION_LABELS) as ProdRegion[]).map((region) => (
            <button
              key={region}
              type="button"
              onClick={() => setSelectedRegion(region)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors duration-150 ${
                selectedRegion === region
                  ? "bg-indigo-600 text-white"
                  : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {REGION_LABELS[region]}
            </button>
          ))}
        </div>

        {selectedRegion && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              {selectedKeys.size > 0
                ? `${selectedKeys.size} selected`
                : "Select keys to batch submit"}
            </span>
            <button
              type="button"
              onClick={handleBatchSubmit}
              disabled={selectedKeys.size === 0}
              className="px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Fill in form
            </button>
          </div>
        )}
      </div>

      {!selectedRegion && (
        <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100">
          <p className="text-xs text-amber-800">
            Pick a target region above to enable batch selection. You can still submit individual keys below.
          </p>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {pagedServiceNames.map((service, groupIdx) => {
          const isOpen = expanded[service] ?? (groupIdx === 0 && page === 1);
          const rowsInGroup = groups[service];

          return (
            <div key={service}>
              <button
                type="button"
                onClick={() => toggleGroup(service)}
                className="w-full px-5 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors duration-150 text-left"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold text-gray-800">{service}</span>
                <span className="text-xs text-gray-500">
                  {rowsInGroup.length} {rowsInGroup.length === 1 ? "key" : "keys"}
                </span>
              </button>

              {isOpen && (
                <div className="px-5 py-3 pl-12 bg-gray-50">
                  <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
                    <div className="grid grid-cols-[auto_2fr_1fr_1fr_1fr_auto]">
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100" />
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">Key</span>
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex justify-center text-xs font-semibold text-gray-500 uppercase tracking-wide">USA</span>
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex justify-center text-xs font-semibold text-gray-500 uppercase tracking-wide">EUR</span>
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex justify-center text-xs font-semibold text-gray-500 uppercase tracking-wide">CAN</span>
                      <span className="px-4 py-2 bg-gray-50 border-b border-gray-100" />

                      {rowsInGroup.map((entry) => {
                        const { rest } = splitServiceAndKey(entry.key);
                        const checkboxEnabled = isMissingFromSelectedRegion(entry);
                        const isChecked = selectedKeys.has(entry.key);

                        return (
                          <React.Fragment key={entry.key}>
                            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={!checkboxEnabled}
                                onChange={() => toggleKey(entry.key)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={`Select ${entry.key} for batch submission`}
                              />
                            </div>
                            <p className="px-4 py-3 border-b border-gray-100 text-sm font-mono text-gray-700 truncate">{rest || entry.key}</p>
                            <div className="px-4 py-3 border-b border-gray-100 flex justify-center items-center">
                              <RegionMark present={entry.usa} />
                            </div>
                            <div className="px-4 py-3 border-b border-gray-100 flex justify-center items-center">
                              <RegionMark present={entry.eur} />
                            </div>
                            <div className="px-4 py-3 border-b border-gray-100 flex justify-center items-center">
                              <RegionMark present={entry.can} />
                            </div>
                            <div className="px-4 py-3 border-b border-gray-100 flex items-center">
                              <button
                                type="button"
                                onClick={() => onSubmit([entry.key])}
                                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors duration-150 whitespace-nowrap"
                              >
                                Fill in form
                              </button>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Ready for staging list — dev has no regions, so this stays a flat key list.
// Supports both individual per-row submit and batch multiselect submit.
// ---------------------------------------------------------------------------

const PromotionList: React.FC<{
  candidates: PromotionCandidate[];
  searchTerm: string;
  emptyMessage: string;
  onPromote: (keys: string[]) => void;
  promoteLabel: string;
}> = ({ candidates, searchTerm, emptyMessage, onPromote, promoteLabel }) => {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return candidates;
    return candidates.filter((c) => c.key.toLowerCase().includes(term));
  }, [candidates, searchTerm]);

  const groups = useMemo(() => groupByService(filtered), [filtered]);
  const serviceNames = useMemo(() => Object.keys(groups).sort(), [groups]);

  const totalPages = Math.max(1, Math.ceil(serviceNames.length / PAGE_SIZE));
  const pagedServiceNames = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return serviceNames.slice(start, start + PAGE_SIZE);
  }, [serviceNames, page]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, candidates]);

  const toggleGroup = (service: string) => {
    setExpanded((prev) => ({ ...prev, [service]: !prev[service] }));
  };

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleBatchSubmit = () => {
    if (selectedKeys.size === 0) return;
    onPromote(Array.from(selectedKeys));
    setSelectedKeys(new Set());
  };

  if (filtered.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm">
        {searchTerm ? "No keys match your search." : emptyMessage}
      </div>
    );
  }

  return (
    <>
      {/* Batch action bar */}
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
        <span className="text-xs text-gray-500">
          {selectedKeys.size > 0 ? `${selectedKeys.size} selected` : "Select keys to batch submit"}
        </span>
        <button
          type="button"
          onClick={handleBatchSubmit}
          disabled={selectedKeys.size === 0}
          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
        >
          Fill in form
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {pagedServiceNames.map((service, groupIdx) => {
          const isOpen = expanded[service] ?? (groupIdx === 0 && page === 1);
          const itemsInGroup = groups[service];

          return (
            <div key={service}>
              <button
                type="button"
                onClick={() => toggleGroup(service)}
                className="w-full px-5 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors duration-150 text-left"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold text-gray-800">{service}</span>
                <span className="text-xs text-gray-500">
                  {itemsInGroup.length} {itemsInGroup.length === 1 ? "key" : "keys"}
                </span>
              </button>

              {isOpen && (
                <div className="divide-y divide-gray-100 bg-gray-50">
                  {itemsInGroup.map((item) => (
                    <div key={item.key} className="px-5 py-3 pl-6 flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(item.key)}
                        onChange={() => toggleKey(item.key)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        aria-label={`Select ${item.key} for batch submission`}
                      />
                      <p className="flex-1 text-sm font-mono text-gray-700 truncate pl-1">
                        {splitServiceAndKey(item.key).rest || item.key}
                      </p>
                      <button
                        type="button"
                        onClick={() => onPromote([item.key])}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors duration-150"
                      >
                        {promoteLabel}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ConfigHealth: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [data, setData] = useState<ConfigHealthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<HealthTab>("drift");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchHealth = (forceRefresh = false) => {
    if (!account) return;
    if (forceRefresh) setRefreshing(true);
    else setIsLoading(true);
    setError(null);
    GetConfigHealth(instance, account, forceRefresh)
      .then((result) => setData(result))
      .catch((err) => setError(err.message || "Failed to load config health data."))
      .finally(() => {
        setIsLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    fetchHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const handlePromote = (keys: string[], fromEnv: string, toEnv: string, region?: string) => {
    window.dispatchEvent(
      new CustomEvent("opsnest:prefill-config-entry", {
        detail: { keys, fromEnv, toEnv, region },
      })
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading config health...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        {error}
        <button
          type="button"
          onClick={() => fetchHealth()}
          className="ml-auto text-xs font-medium underline hover:text-rose-900"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const driftedRows = data.regional_drift.filter((r) => !(r.usa && r.eur && r.can));
  const driftCount = driftedRows.length;
  const stagingCount = data.ready_for_staging.length;
  const prodCount = data.ready_for_production.length;

  const tabs: { id: HealthTab; label: string; count: number; danger: boolean }[] = [
    { id: "drift", label: "Drift", count: driftCount, danger: driftCount > 0 },
    { id: "staging", label: "Ready for staging", count: stagingCount, danger: false },
    { id: "production", label: "Ready for production", count: prodCount, danger: false },
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-indigo-100 shadow-lg bg-white">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Config health</h3>
          <p className="text-sm text-gray-500">Promotion candidates and production drift</p>
        </div>
        <button
          type="button"
          onClick={() => fetchHealth(true)}
          disabled={refreshing}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-150"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Tab bar */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
              activeTab === tab.id
                ? "bg-indigo-600 text-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            {tab.label}
            <span
              className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                activeTab === tab.id
                  ? "bg-white/20 text-white"
                  : tab.danger
                  ? "bg-rose-100 text-rose-800"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tab description */}
      <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-500">
          {activeTab === "drift" &&
            "Keys present in some production regions but missing from others. Any entry here is a bug — the three regions should always match."}
          {activeTab === "staging" &&
            "Keys that exist in dev but haven't been submitted to staging yet."}
          {activeTab === "production" &&
            "Keys that exist in staging but aren't yet present in all three production regions."}
        </p>
      </div>

      {/* Search */}
      <div className="px-5 py-3 border-b border-gray-100">
        <input
          type="text"
          placeholder="Search keys"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Active tab content */}
      {activeTab === "drift" && <DriftList rows={driftedRows} searchTerm={searchTerm} />}

      {activeTab === "staging" && (
        <PromotionList
          candidates={data.ready_for_staging}
          searchTerm={searchTerm}
          emptyMessage="Staging is caught up with dev."
          promoteLabel="Fill in form"
          onPromote={(keys) => handlePromote(keys, "development", "staging")}
        />
      )}

      {activeTab === "production" && (
        <ProductionReadyList
          rows={data.ready_for_production}
          searchTerm={searchTerm}
          emptyMessage="Production is caught up with staging."
          onSubmit={(keys, region) => handlePromote(keys, "staging", "production", region)}
        />
      )}
    </div>
  );
};

export default ConfigHealth;